import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { MeetingQuestion } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { posLabel, type PosCtx } from "../lib/format";
import { Avatar } from "./ui";
import { AskIcon, CheckIcon, LockIcon, MoreIcon, SendIcon } from "./Icons";
import { meeting } from "@pagemates/shared/copy";

const txt = meeting.meetingQuestions;

/** Invalidate everything that lists questions. */
export const refreshQuestions = (qc: ReturnType<typeof useQueryClient>, clubId: string) =>
  Promise.all([qc.invalidateQueries({ queryKey: ["questions", clubId] }), qc.invalidateQueries({ queryKey: ["meeting-detail", clubId] })]);

/**
 * "Bring a question…": a one-line box that grows into a small composer with
 * the position chip (where you are, spoiler-free, or a landmark).
 */
export function QuestionBox({ clubId, herePercent, ctx, placeholder = txt.bringAQuestion }: { clubId: string; herePercent: number; ctx: PosCtx; placeholder?: string }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"here" | "open" | number>("here");
  const [pick, setPick] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const position = mode === "here" ? herePercent : mode === "open" ? 0 : mode;
  const post = useMutation({
    mutationFn: () => api.addQuestion(clubId, { body, position }),
    onSuccess: async () => { setBody(""); setOpen(false); setMode("here"); if (ta.current) ta.current.style.height = "auto"; await refreshQuestions(qc, clubId); },
  });
  const chip = position === 0 ? txt.open : posLabel(position, ctx);
  return (
    <form className={`qbox${open ? " open" : ""}`} onSubmit={(e) => { e.preventDefault(); if (body.trim()) post.mutate(); }}
      onBlur={(e) => { if (!body.trim() && !e.currentTarget.contains(e.relatedTarget as Node | null)) { setOpen(false); setPick(false); } }}>
      <AskIcon width={18} height={18} className="qbox-ico" />
      <textarea ref={ta} id="bring-question" rows={1} value={body} placeholder={placeholder} maxLength={500}
        onFocus={() => setOpen(true)} onChange={(e) => { setBody(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 7.2 * 16)}px`; }}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); if (body.trim()) post.mutate(); } }} />
      <div className="qbox-tail">
        <button type="button" className={`chip${position === 0 ? " open" : ""}`} aria-expanded={pick} onClick={() => setPick((p) => !p)}>{chip}</button>
        {(open || body.trim()) && <button type="submit" className="send" disabled={!body.trim() || post.isPending} aria-label={txt.bringIt}><SendIcon /></button>}
      </div>
      {pick && (
        <div className="popover" role="radiogroup" aria-label={txt.whoCanSeeThis}>
          <button type="button" role="radio" aria-checked={mode === "open"} onClick={() => { setMode("open"); setPick(false); }}><b>{txt.spoilerFree}</b><span>{txt.everyoneSeesIt}</span></button>
          <button type="button" role="radio" aria-checked={mode === "here"} onClick={() => { setMode("here"); setPick(false); }}><b>{txt.whereIAm(herePercent)}</b><span>{txt.sealedForAnyoneWho}</span></button>
          {(ctx.landmarks?.length ?? 0) > 0 && <div className="grp">{txt.landmarks}</div>}
          {ctx.landmarks?.map((l) => (
            <button key={l.name + l.percent} type="button" role="radio" className="lm" aria-checked={mode === l.percent} onClick={() => { setMode(l.percent); setPick(false); }}><b>{txt.through(l.name)}<small>{l.percent}%</small></b></button>
          ))}
        </div>
      )}
      {post.isError && <p className="error small" style={{ gridColumn: "1 / -1" }}>{describeError(post.error)}</p>}
    </form>
  );
}

/** One question: author, position, the text (or a sealed row), and for the author an edit/remove menu. */
export function QuestionRow({ clubId, q, ctx, tick, onTick }:
  { clubId: string; q: MeetingQuestion; ctx: PosCtx; tick?: boolean; onTick?: (done: boolean) => void }) {
  const qc = useQueryClient();
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(q.body ?? "");
  const ref = useRef<HTMLDivElement>(null);
  const save = useMutation({ mutationFn: () => api.editQuestion(clubId, q.id, { body: draft }), onSuccess: async () => { setEditing(false); await refreshQuestions(qc, clubId); } });
  const del = useMutation({ mutationFn: () => api.deleteQuestion(clubId, q.id), onSuccess: () => refreshQuestions(qc, clubId) });
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);
  if (q.locked) {
    return (
      <div className={`q sealed${tick ? " tickable" : ""}`}>
        {tick ? <span className="tick ghost" aria-hidden="true" /> : <Avatar name={q.author.name} image={q.author.image} />}
        <div><div className="t"><LockIcon width={14} height={14} />{txt.sealedUntil(posLabel(q.position, ctx), q.author.name)}</div></div>
      </div>
    );
  }
  return (
    <div className={`q${q.discussed ? " done" : ""}${tick ? " tickable" : ""}`} ref={ref}>
      {tick && (
        <button type="button" className={`tick${q.discussed ? " on" : ""}`} aria-pressed={q.discussed} aria-label={q.discussed ? txt.markNotDiscussed : txt.markDiscussed} onClick={() => onTick?.(!q.discussed)}>
          {q.discussed && <CheckIcon width={16} height={16} />}
        </button>
      )}
      {!tick && <Avatar name={q.author.name} image={q.author.image} />}
      <div style={{ minWidth: 0 }}>
        {editing ? (
          <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) save.mutate(); }}>
            <textarea className="input" value={draft} maxLength={500} rows={2} onChange={(e) => setDraft(e.target.value)} autoFocus />
            <div className="row"><button type="submit" className="btn primary sm" disabled={!draft.trim() || save.isPending}>{txt.save}</button><button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>{txt.cancel}</button></div>
            {save.isError && <p className="error small">{describeError(save.error)}</p>}
          </form>
        ) : (
          <>
            <div className="t">{q.body}</div>
            <div className="d">{tick && <Avatar name={q.author.name} image={q.author.image} />}<span>{q.mine ? txt.you : q.author.name}</span>{q.position > 0 && <span className={`pos${q.mine ? " mine" : ""}`}>{posLabel(q.position, ctx)}</span>}{q.carriedOver ? <span>{txt.fromLastTime}</span> : null}{q.discussed && !tick ? <span>{txt.discussed}</span> : null}</div>
          </>
        )}
        {del.isError && <p className="error small">{describeError(del.error)}</p>}
      </div>
      {q.mine && !q.discussed && !editing && (
        <>
          <button type="button" className="more" aria-label={txt.more} aria-expanded={menu} onClick={() => setMenu((m) => !m)}><MoreIcon width={18} height={18} /></button>
          {menu && (
            <div className="menu" role="menu">
              <button role="menuitem" onClick={() => { setMenu(false); setDraft(q.body ?? ""); setEditing(true); }}>{txt.edit}</button>
              <button role="menuitem" className="danger" onClick={() => { setMenu(false); del.mutate(); }}>{txt.remove}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
